import { Provider } from 'react-redux';

// Side-effect import: registers the eager `common`, `uiKit` and `permissions`
// catalogs. It belongs HERE rather than in `main.tsx` because this is the
// component that renders translated copy — a composition root should guarantee
// its own preconditions, not inherit them from whichever file happened to mount
// it. Without it, anything that renders <App /> outside the entry point (a spec,
// a future preview harness) paints raw keys like `shell.title`.
import '@/shared/i18n';
import { initializeAppConfig } from '@/app.config';
import { registerStoreApis } from '@/store.config';
import { AppRouter } from '@/routes';
import { createAppStore } from '@/shared/store/store-factory/store-factory';
import { DirectionProvider } from '@/shared/theme/DirectionProvider/DirectionProvider';
import { ThemeProvider } from '@/shared/theme/ThemeProvider/ThemeProvider';

import { ConfirmationRoot } from '@/shared/components/feedback/ConfirmationRoot/ConfirmationRoot';
import { Toaster } from '@/shared/components/feedback/Toaster/Toaster';
import { GlobalModalManager } from '@/shared/components/Modals/GlobalModalManager/GlobalModalManager';

/**
 * The composition root.
 *
 * Order below is load-bearing, not stylistic:
 *
 *   registerStoreApis()  — apis must be in the registry BEFORE the store is built
 *   initializeAppConfig() — catalogues and modal types before the first render
 *   createAppStore()      — reads the persisted session and preferences once
 *
 *   Provider (store)   — everything below reads preferences from it
 *     ThemeProvider    — resolves `system` against the OS and paints `.dark`
 *       DirectionProvider — sets lang/dir and tells Radix which way is `start`
 *         AppRouter     — the public /login route and ONE protected shell route
 *
 * All three of those calls happen at MODULE scope rather than in an effect. An
 * effect runs after the first render, which means the store would be built
 * without its api reducers and the first paint would show untranslated keys —
 * and under StrictMode it would run twice.
 *
 * `main.tsx` has already written theme and direction onto `<html>` before this
 * renders, so nothing here causes a first-paint flash.
 *
 * The three singletons are mounted exactly ONCE, beside the router rather than
 * inside it: each portals to the document body, and a second copy would double
 * every toast and race two confirmation dialogs over one resolver. They sit
 * inside `DirectionProvider` because every one of them is direction-aware — the
 * toast viewport docks to the inline end, the sheet slides from the inline
 * start, the dialog aligns from the inline start.
 */
registerStoreApis();
initializeAppConfig();

const store = createAppStore();

export function App(): React.JSX.Element {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <DirectionProvider>
          <AppRouter />
          <GlobalModalManager />
          <ConfirmationRoot />
          <Toaster />
        </DirectionProvider>
      </ThemeProvider>
    </Provider>
  );
}
